import { Modal, Spin } from 'antd';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import importComponents from '../../utils/importComponents';
import { ModuleNames } from '../../interfaces';
import './layout.less';
import useCssVariables from '../../hooks/useCssVariables';
import classPriority from '../../utils/classPriority';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import EditComponent from '../../utils/EditComponent';
import EditLayoutComponent from '../../utils/EditLayoutComponent';
import { useSubscriptionPlanGuard } from '@skygroup/shared/components/subscription-plan-guard/useSubscriptionPlanGuard';
import { setIsUpgradePlanModalOpen } from '../../store/config';
import { PlanIdType } from '@skygroup/shared/constants/agency';
import { upgradeRequestApi } from '../AgentUserBanner/AgentApi';
import {
  ApiStatus,
  ApiStatusMessage,
} from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import UpgradePlanTemplate from '@skygroup/shared/components/subscription-plan-guard/UpgradePlanTemplate';
import UpdateCardWarning from '@skygroup/shared/components/subscription-plan-guard/UpdateCardWarning';
import MembershipLoginForm from '../../modules/Auth/MembershipLoginForm/MembershipLoginForm';
import OTPForm from '../../modules/Auth/OtpForm/OtpForm';
import { useSearchParams } from 'react-router-dom';
import ModuleConfigPlaceholder from '../ModuleConfigPlaceholder/ModuleConfigPlaceholder';
import LazyImportFallback from '../../modules/LazyImportFallback/LazyImportFallback';
import { mapComponents } from './mapComponents';
import { getInstanceNamesFromPageModules } from '@skygroup/shared/utils/modulesConversion';

const baseLayoutNames = ['default', 'empty', 'noFooter'];
interface Props {
  pageId: string;
  pagePath: string;
  pageComponents: string[];
  layout: string;
  isPreviewMode?: boolean;
  selectedInstance: string | null;
  setSelectedInstance: React.Dispatch<React.SetStateAction<string | null>>;
  dynamicModules?: any;
  page?: any,
  promotionRes?: any;
}

const Layout = ({
  pageId,
  pagePath,
  pageComponents,
  layout,
  isPreviewMode,
  selectedInstance,
  setSelectedInstance,
  dynamicModules,
  page,
  promotionRes
}: Props) => {
  const dispatch = useDispatch();
  const [componentsState, setComponents] = useState<any>();
  const { modulesConfigs, layouts, theme, pages, siteConfig } = useSelector(
    (data: RootState) => data.config.config
  );
  const [isEditModeOn, setIsEditModeOn] = React.useState<string | null>(null);
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('editMode') === 'true';
// inside component (after you have pageComponents prop)
const instanceNames = useMemo(() => getInstanceNamesFromPageModules(pageComponents), [pageComponents]);

  const footerComponents = useMemo(() => {
    return importComponents(layouts[layout]?.bottomModules, modulesConfigs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layouts, layout]);
const components = useMemo(() => {
  return importComponents(instanceNames, dynamicModules || modulesConfigs);
}, [instanceNames, modulesConfigs]);

  const headerComponents = useMemo(() => {
    return importComponents(layouts[layout].topModules, modulesConfigs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layouts, layout]);

  useEffect(() => {
    setComponents(components);
  }, [components]);

  const isAllowedToEdit = ['PackageListView', 'FlightList', 'SwitchDatePicker'];
  const isAllowedToEditLayout = ['Footer'];

  const [openedModalData, setOpenedModalData] = useState<null | {
    type: 'update-card' | 'upgrade-plan';
    data?: any;
  }>(null);
  const [apiStatusMessage, setApiStatusMessage] = useState(
    {} as ApiStatusMessage
  );
  const {
    isOtpAuthCheckModalOpen,
    isUpgradePlanModalOpen,
    isLayoutModalOpen,
    agentInfo,
    translations: t,
  } = useSelector((data: RootState) => data.config);
  const { handleNewTab } = useSubscriptionPlanGuard({
    subscriptionObj: agentInfo?.agency?.subscription,
  });

  const handleUpgradeOnClick = async (
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      const res = await upgradeRequestApi({
        plan_id: agentInfo?.agency?.subscription?.plan_id,
        setIsLoading: setIsLoading,
      });
      if (res.status === 'success') {
        setApiStatusMessage({ status: ApiStatus.success, data: res.data });
        // Move to upgrade page
        handleNewTab();
        return { success: true };
      } else {
        setApiStatusMessage({ status: ApiStatus.error, data: res.data });
      }
    } catch (error: any) {
      setApiStatusMessage({ status: ApiStatus.error, data: error });
    }
    return { success: false };
  };

  const handleModalCancel = () => {
    if (!!openedModalData?.data?.disabledClose) {
      return;
    }
    dispatch(setIsUpgradePlanModalOpen(null));
  };

  useEffect(() => {
    setOpenedModalData(isLayoutModalOpen);
  }, [isLayoutModalOpen]);

  const isLight = agentInfo?.agency?.subscription?.plan_id === PlanIdType.LIGHT;

  const mapComponentsLayout = (
    componentsArray: {
      Component: React.LazyExoticComponent<React.ComponentType<any>>;
      ComponentName: any;
    }[],
    layoutSection: string
  ) => {
    if (!componentsArray?.length) {
      return <ModuleConfigPlaceholder />;
    }

    return componentsArray?.map((component: any, i: number) => {
      const {
        Component,
        ComponentName,
      }: { Component: any; ComponentName: ModuleNames } = component;

      const instance = ComponentName;
      const moduleName = modulesConfigs[ComponentName]?.module;
      const componentModuleConfig =
        modulesConfigs[instance] || modulesConfigs[moduleName];
      // const moduleName = modulesConfigs?.[ComponentName]?.module ?? ComponentName;

      return (
        <div
          id={`${instance}-layout-wrapper`}
          className={ComponentName}
          style={{ marginBottom: ComponentName === 'Header' ? 0 : 90 }}
          key={i}
        >
          {ComponentName &&
            modulesConfigs[ComponentName]?.theme &&
            generateCssVariables(
              `.${ComponentName}`,
              modulesConfigs[ComponentName].theme as any
            )}
          <EditLayoutComponent
            layoutSection={layoutSection}
            pageId={pageId}
            pageComponents={pageComponents}
            instance={instance}
            moduleName={moduleName}
            key={`${i}_${instance}`}
            index={String(i)}
            selectedInstance={selectedInstance}
            setSelectedInstance={setSelectedInstance}
            layout={layout}
            isEditAllowed={moduleName != "Footer" ? modulesConfigs[ComponentName]?.allow_edit !== false : false}
            config={modulesConfigs[ComponentName]}
            // isEditAllowed={true}
          >
            <Component
              config={modulesConfigs[ComponentName]}
              componentStyle={classPriority(
                theme,
                modulesConfigs[ComponentName]
              )}
            />
          </EditLayoutComponent>
        </div>
      );
    });
  };

  const generateCssVariables = useCssVariables();

  return (
    <div className={`app-wrapper ${isEditMode ? 'isEditMode' : ''}`}>
      <Suspense
        fallback={<Spin className="center lazy-loader-spinner" size="large" />}
      >
        {!isPreviewMode && mapComponentsLayout(headerComponents, 'topModules')}
        {mapComponents(
          componentsState,
          dynamicModules || modulesConfigs,
          theme,
          pageId,
          pagePath,
          instanceNames,
          setComponents,
          selectedInstance,
          setSelectedInstance,
          generateCssVariables,
          dynamicModules ? true : false,
          page,
          promotionRes
        )}
        {!isPreviewMode &&
          mapComponentsLayout(footerComponents, 'bottomModules')}

        {openedModalData?.type && (
          <Modal
            className={`upgrade-plan-template-modal`}
            open={!!openedModalData?.type}
            footer={false}
            onCancel={handleModalCancel}
            style={{ background: '#F5F5F5' }}
            centered
            closable={!openedModalData?.data?.disabledClose}
          >
            {openedModalData?.type === 'upgrade-plan' && (
              <UpgradePlanTemplate
                renderProPlan={
                  agentInfo?.agency?.subscription?.plan_id ===
                  PlanIdType.REGULAR
                }
                upgradePlan_t={
                  isLight ? t?.upgradePlan?.regular : t?.upgradePlan?.pro
                }
                handleUpgradeOnClick={handleUpgradeOnClick}
                apiStatusMessage={apiStatusMessage}
                crud_t={t.crud}
              />
            )}

            {openedModalData?.type === 'update-card' && (
              <UpdateCardWarning updateCard_t={t?.updateCard} />
            )}
          </Modal>
        )}
        {/* auth modal */}
        {isOtpAuthCheckModalOpen && (
          <Modal
            className={`upgrade-plan-template-modal`}
            open={isOtpAuthCheckModalOpen}
            title={
              <div className={'membership-login-header'}>
                <h2>{siteConfig?.membership?.title}</h2>
                {t?.auth.authTitle}
              </div>
            }
            footer={null}
            style={{ background: '#F5F5F5' }}
            centered
            closable={false}
            maskClosable={false}
          >
            {!isLoginSuccess ? (
              <MembershipLoginForm
                setIsLoginSuccess={setIsLoginSuccess}
                setSessionId={setSessionId}
              />
            ) : (
              <OTPForm
                setIsLoginSuccess={setIsLoginSuccess}
                sessionId={sessionId!}
              />
            )}
          </Modal>
        )}
        {/*<LayoutModalsWrapper />*/}
      </Suspense>
    </div>
  );
};

export default Layout;